import { ISupportCategory, SupportCategoryMap } from '@belong/types/support';
import { FImage } from '@belong/contentful';
import { withoutBasePath } from '@belong/url-utils';

export function Category({ fields, sys }: any): ISupportCategory {
  const { label, slug, icon, childCategories = [] } = fields;
  const { pageTitle, categoryImage, metaDescription, metaKeywords } = fields;
  const { id } = sys;
  return {
    id,
    label,
    slug,
    icon,
    metaDescription,
    metaKeywords,
    pageTitle,
    categoryImage: FImage(categoryImage),
    children: childCategories.map(Category)
  };
}

export default class CategoryModel {
  public root: ISupportCategory = {} as ISupportCategory;
  private paths: SupportCategoryMap = {};
  private items: SupportCategoryMap = {};

  private parseCategory(category: ISupportCategory, root = ''): void {
    category.path = `${root}/${category.slug}`;

    this.items[category.id] = category;
    this.paths[category.path] = category;
    category.children.forEach(item => this.parseCategory(item, category.path));
  }

  // Rehydrate class from pre-parsed data
  private rehydrate({ paths, items, root }): void {
    this.paths = paths;
    this.items = items;
    this.root = root;
  }

  constructor(data: any) {
    if (['paths', 'items', 'root'].every(field => field in data)) {
      this.rehydrate(data);
      return;
    }

    const { id, slug, pageTitle, label, categoryImage } = data;
    this.root = {
      id,
      slug,
      pageTitle,
      label,
      categoryImage,
      children: []
    };

    // parse raw data
    data.childCategories.map(Category).forEach(item => this.parseCategory(item));
  }

  /**
   * Find a category from its contentful id
   * @example
   * getCategoryById('5bBk4')
   */
  public getCategoryById(id: string): ISupportCategory | undefined {
    return this.items[id];
  }

  /**
   * Find a category from a hierarchical path by validating against the category model
   * Use this to find out if a path is a valid category or if it's an article url
   * Note - ensure we strip off query or hash before path lookup
   * @example
   * getCategoryByPath('/parent/child')
   */
  public getCategoryByPath(path = ''): ISupportCategory | null {
    try {
      return this.paths[withoutBasePath(path)];
    } catch (err) {
      return null;
    }
  }

  /**
   * Return the full path for a category
   */
  public getPathFromCategory(id = ''): string | null {
    try {
      const { slug } = this.items[id];
      const [path] = Object.entries(this.paths).find(([key, val]) => key.endsWith(slug) && val.id === id) || [null];
      return path;
    } catch (err) {
      return null;
    }
  }

  /**
   * Find the parent category from a hierarchical path
   */
  public getParentCategory(path = ''): ISupportCategory | undefined {
    const parentPath = path.replace(/\/[^/]+$/, '');
    return this.paths[parentPath];
  }

  /**
   * Find a TOP-LEVEL category from a hierarchical path
   */
  public getRootCategory(path = ''): ISupportCategory {
    const categoryPath = (withoutBasePath(path).match(/^(\/[^/]+)\/?/) || [])[1];
    return this.paths[categoryPath];
  }
}
